/**
 * @author Bismark A. FRIMPONG
 * @email info@bafrimpong.com
 * @create date 2024-05-20
 * @modify date 2024-05-27
 * @desc Academy-v2 implementation of Mixpanel to track user clicks on some specific buttons and links for analytics
 */
import mixpanel from "mixpanel-browser";
import dayjs from "dayjs";
import { LoginUserItem } from "@/types";

// Mixpanel token
const MIXPANEL_TOKEN = import.meta.env.EDS_MIXPANEL_TOKEN;

const MIXPANEL_BTN_CLICKED = "Button Clicked";
const MIXPANEL_LNK_CLICKED = "Link Clicked";
const MIXPANEL_USR_PROFILE = "User Profile";
const MIXPANEL_DIV_BTN_CLICKED = "Calendar Item Clicked";
const MIXPANEL_PAGE_VERSION_MOBILE = "Mobile";
const MIXPANEL_PAGE_VERSION_DESKTOP = "Desktop";

export const MIXPANEL_EVENT_TYPES = {
  ONE_TO_ONE: "One to One",
  SPEAKING: "Speaking",
  MOCK_TEST: "Mock Test",
  WRITING_CORRECTION: "Writing Correction",
  GRAMMAR_AND_IELTS: "Grammar and IELTS",
  DASHBOARD_SIDEBAR_LINK: "Dashboard Sidebar Links",
  SIDEBAR_MENU_AGENDA: "Menu_Agenda",
  SIDEBAR_MENU_HOME: "Menu_Home",
  SIDEBAR_MENU_EXERCISES: "Menu_Exercises",
  SIDEBAR_MENU_SUPPORT: "Menu_Support",
  SIDEBAR_MENU_LOGOUT: "Menu_Logout",
  SIDEBAR_MENU_MATERIALS: "Menu_Materials",
  SIDEBAR_MENU_ONE_TO_ONE_CLASSES: "Menu_OneToOne_Classes",
  SIDEBAR_MENU_DIPLOMA_REQUEST: "Menu_Diploma_Request",
  SIDEBAR_MENU_CLASS_REPORT: "Menu_Class_Report",
  SIDEBAR_MENU_INVOICES_AND_CONTRACT: "Menu_Invoices_And_Contracts",
  SIDEBAR_MENU_AVAILABILITY: "Menu_Availability",
  SIDEBAR_MENU_REFER_FRIEND: "Menu_Refer_Friend",
  SIDEBAR_MENU_BOOK_EXAM: "Menu_Book_Exam",
  UPSELLING_CONFIRM: "Upselling_Confirm",
  UPSELLING_CANCEL: "Upselling_Cancel",
  UPSELLING_REJECT: "Upselling_Reject",
  EXERCISE_DO_IT_BUTTON: "Exercise_Do_It",
  EXERCISE_TO_DO_BUTTON: "Exercise_To_Do",
  EXERCISE_DONE_BUTTONE: "Exercise_Done",
  EXERCISE_EXPIRES_FIRST: "Exercise_Expires_First",
  EXERCISE_VIEW: "Exercise_View",
  EXERCISE_BOOK_LIVE_CORRECTION: "Exercise_Book_Live_Correction",
  EXERCISE_FILTER_DROPDOWN: "Exercise_Filter_Selection"
}


// initialize Mixpanel with its token and optional configuration
mixpanel.init(MIXPANEL_TOKEN, {
  debug: true, // Enable debug mode for logging Mixpanel activities in the console
  //track_pageview: "full-url",
  persistence: "localStorage",
});

/**
 * Calculate the user age from the date of birth using momentjs library
 * @param dateOfBirth {string} the date of birth of the user
 * @returns {number} returns the user age as a number
 */
const getUserAge = (dateOfBirth: string): number => {
  return dayjs().diff(dayjs(dateOfBirth), "years");
};

export type EventProps = {
  pageVisited: string;
  name: string;
  email: string;
  dateOfBirth: string;
  age: string;
  language: string;
  englishLevel: string;
  products: [];
};

export type UserObject = {
  email: string;
  name: string;
};

interface Product {
  id: number;
  product_tag: string;
  price: number;
}

export interface MixpanelUser {
  first_name: string;
  last_name: string;
  email: string;
  birth_date: string;
  lang: string;
  english_level: string;
  products: Array<{
    id: number;
    name: string;
    price: number;
    product_tag: string;
  }>;
}

/**
 * Gets an event name as parameter an checks it to return the appropriate event name as string
 * @param eventName name of the event
 * @returns a string of the event name
 */
export const getSidebarEventName = (eventName: string): string => {
  switch (eventName) {
    case "Home":
      return MIXPANEL_EVENT_TYPES.SIDEBAR_MENU_HOME;
    case "Agenda":
      return MIXPANEL_EVENT_TYPES.SIDEBAR_MENU_AGENDA;
    case "Exercises":
      return MIXPANEL_EVENT_TYPES.SIDEBAR_MENU_EXERCISES;
    case "Materials":
      return MIXPANEL_EVENT_TYPES.SIDEBAR_MENU_MATERIALS;
    case "1:1 classes":
      return MIXPANEL_EVENT_TYPES.SIDEBAR_MENU_ONE_TO_ONE_CLASSES;
    case "Diploma request":
      return MIXPANEL_EVENT_TYPES.SIDEBAR_MENU_AGENDA;
    case "Class report":
      return MIXPANEL_EVENT_TYPES.SIDEBAR_MENU_AGENDA;
    case "Invoices & contract":
      return MIXPANEL_EVENT_TYPES.SIDEBAR_MENU_INVOICES_AND_CONTRACT;
    case "Availability":
      return MIXPANEL_EVENT_TYPES.SIDEBAR_MENU_AVAILABILITY;
    case "Book exam":
      return MIXPANEL_EVENT_TYPES.SIDEBAR_MENU_BOOK_EXAM;
    default:
      return MIXPANEL_EVENT_TYPES.SIDEBAR_MENU_REFER_FRIEND;
  }
};

/**
 * Creates a mixpanel data object for the event tracking and user procfile creation
 * @param pageVisited {string} the name of the page visited
 */
export const maixpanelTrackEventProps = (
  pageVisited: string,
  me: LoginUserItem,
) => {
  //Get all product(s) related to a user record
  const getProducts = () => {
    const products = me?.products;

    const prods = products?.map((prod: Product, index: number) => {
      const product = { id: 0, name: "", price: 0 };

      product.id = prod?.id || products[index].id;
      product.name = prod?.product_tag || products[index].product_tag;
      product.price = prod?.price || products[index].price;

      return product;
    });

    return prods;
  };

  const listOfProds = getProducts(); // Object.assign({}, getProducts())
  
  // create an object literal for mixpanel processing
  const data = {
    eventProperties: {
      pageVisited: pageVisited,
      name: `${me?.first_name} ${me?.last_name.toString().toUpperCase()}`,
      email: me?.email.toString(),
      dateOfBirth: me?.birth_date,
      age: getUserAge(me?.birth_date?.toString() || Date.now().toString()),
      language: me?.lang,
      englishLevel: me?.english_level,
      products: listOfProds || {
        info: "User has not yet subcribed for a product",
      },
    },
    userProfile: {
      name: `${me?.first_name} ${me?.last_name.toString().toUpperCase()}`,
      email: me?.email,
    },
  };

  return data;
};

/**
 * Creates a mixpanel event to track all events
 * @param eventName {string} event name such as buttonClick, linkClick etc.
 * @param properties {{}} an object literal
 */
export const trackEvent = (eventName: string, properties: EventProps) => {
  mixpanel.track(eventName, properties);
};

/**
 * Create a mixpanel user profile for the tracked event
 * @param userProps {{}} a property of an object for setting up the user profile
 */
export const identifyUser = (userProps: UserObject) => {
  const email = userProps?.email;
  const name = userProps?.name;

  mixpanel.identify(email);
  // mixpanel.alias(email);
  mixpanel.people.set({
    name: name,
    $email: email,
  });
};

export {
  MIXPANEL_BTN_CLICKED,
  MIXPANEL_LNK_CLICKED,
  MIXPANEL_USR_PROFILE,
  MIXPANEL_DIV_BTN_CLICKED,
  MIXPANEL_PAGE_VERSION_DESKTOP,
  MIXPANEL_PAGE_VERSION_MOBILE,
};
